import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../lib';
import {
  type AIChatMessage,
  fetchAIFilters,
  fetchVectorSearch,
  type GetAIChatHistoryRequest,
  type GetAIChatHistoryResponse,
  type GetAIFiltersRequest,
  type GetAIFiltersResponse,
  getAIChatHistory,
  type SendAIChatMessageRequest,
  type SendAIChatMessageResponse,
  type StartAIChatRequest,
  type StartAIChatResponse,
  sendAIChatMessage,
  startAIChat,
  type VectorSearchRequest,
  type VectorSearchResponse,
} from '../../lib/api/aiFeatures';

export enum AI_API_TAGS {
  GetAIFilters = 'GetAIFilters',
  VectorSearch = 'VectorSearch',
  StartAIChat = 'StartAIChat',
  GetAIChatHistory = 'GetAIChatHistory',
  SendAIChatMessage = 'SendAIChatMessage',
}

export function useLazyGetAIFiltersQuery() {
  const mutation = useMutation<GetAIFiltersResponse, unknown, GetAIFiltersRequest>({
    mutationFn: fetchAIFilters,
    mutationKey: [AI_API_TAGS.GetAIFilters],
  });

  return {
    ...mutation,
    trigger: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isFetching: mutation.isPending,
  };
}

export function useVectorSearchQuery(body: VectorSearchRequest, options?: { enabled?: boolean }) {
  return useQuery<VectorSearchResponse>({
    queryKey: [AI_API_TAGS.VectorSearch, body],
    queryFn: () => fetchVectorSearch(body),
    enabled: options?.enabled ?? true,
  });
}

export function useStartAIChatMutation() {
  return useMutation<StartAIChatResponse, unknown, StartAIChatRequest>({
    mutationKey: [AI_API_TAGS.StartAIChat],
    mutationFn: (body) => startAIChat(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AI_API_TAGS.GetAIChatHistory] });
    },
  });
}

export function useSendAIChatMessageMutation() {
  return useMutation<
    SendAIChatMessageResponse,
    unknown,
    SendAIChatMessageRequest,
    { previousHistory?: GetAIChatHistoryResponse }
  >({
    mutationKey: [AI_API_TAGS.StartAIChat],
    mutationFn: (body) => sendAIChatMessage(body),
    onMutate: async ({ chatId, workspaceId, message }) => {
      await queryClient.cancelQueries({ queryKey: [AI_API_TAGS.GetAIChatHistory] });

      const previousHistory = queryClient.getQueryData<GetAIChatHistoryResponse>([AI_API_TAGS.GetAIChatHistory]);

      const newUserMessage: AIChatMessage = {
        message_id: 0,
        content: message,
        created_at: new Date().toISOString(),
        role: 'user',
        summarized: false,
      };

      const newAIMessageBubble: AIChatMessage = {
        message_id: -1,
        content: '%CHAT_BUBBLE%',
        created_at: new Date().toISOString(),
        role: 'model',
        summarized: false,
      };

      queryClient.setQueryData<GetAIChatHistoryResponse>(
        [AI_API_TAGS.GetAIChatHistory, { chatId, workspaceId }],
        (old) => (old ? { ...old, messages: [...old.messages, newUserMessage, newAIMessageBubble] } : undefined),
      );

      return { previousHistory };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData([AI_API_TAGS.GetAIChatHistory], context?.previousHistory);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [AI_API_TAGS.GetAIChatHistory] });
    },
  });
}

export function useAIChatHistoryQuery(params: Partial<GetAIChatHistoryRequest>) {
  return useQuery<GetAIChatHistoryResponse>({
    queryKey: [AI_API_TAGS.GetAIChatHistory, params],
    queryFn: () => getAIChatHistory(params as GetAIChatHistoryRequest),
    enabled: Object.values(params).every(Boolean),
  });
}
